import * as React from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "motion/react";

import { CursorImageTrail } from "@/components/base/CursorImageTrail";

const trailEmojis = [
  "✨",
  "🌈",
  "🔥",
  "💫",
  "🌟",
  "🎨",
  "🍭",
  "⚡️",
  "🌸",
  "🎈",
  "💥",
  "🪩",
];

const navLinks = [
  { label: "Home", href: "#" },
  { label: "Features", href: "#features" },
  { label: "Playground", href: "#playground" },
  { label: "Docs", href: "#docs" },
];

type ThemeKey = "light" | "dark" | "paper" | "universe" | "cloth" | "silver";

interface Theme {
  key: ThemeKey;
  label: string;
  icon: string;
  bgClass: string;
  textPrimary: string;
  textMuted: string;
  chipBorder: string;
  chipBg: string;
  chipText: string;
  chipDot: string;
  navHover: string;
  signIn: string;
  ctaBg: string;
  ctaText: string;
  ctaHover: string;
  logoMarkBg: string;
  logoMarkText: string;
  logoText: string;
  shuffleBg: string;
  shuffleText: string;
  shuffleBorder: string;
  shimmerBase: string;
  shimmerHighlight: string;
}

const themes: Theme[] = [
  {
    key: "light",
    label: "Light",
    icon: "ri-sun-line",
    bgClass: "theme-light",
    textPrimary: "text-foreground-950",
    textMuted: "text-foreground-700",
    chipBorder: "border-background-200",
    chipBg: "bg-background-100/60",
    chipText: "text-foreground-700",
    chipDot: "bg-primary-500",
    navHover: "hover:text-foreground-950",
    signIn: "text-foreground-700 hover:text-foreground-950",
    ctaBg: "bg-primary-500 hover:bg-primary-600",
    ctaText: "text-background-50",
    ctaHover: "",
    logoMarkBg: "bg-primary-500",
    logoMarkText: "text-background-50",
    logoText: "text-foreground-950",
    shuffleBg: "bg-neutral-900 hover:bg-neutral-800 shadow-lg shadow-neutral-900/20",
    shuffleText: "text-white",
    shuffleBorder: "border-neutral-900",
    shimmerBase: "oklch(0.18 0 0)",
    shimmerHighlight: "oklch(var(--primary-500))",
  },
  {
    key: "dark",
    label: "Dark",
    icon: "ri-moon-line",
    bgClass: "theme-dark",
    textPrimary: "text-white",
    textMuted: "text-white/70",
    chipBorder: "border-white/15",
    chipBg: "bg-white/5",
    chipText: "text-white/80",
    chipDot: "bg-fuchsia-400",
    navHover: "hover:text-white",
    signIn: "text-white/70 hover:text-white",
    ctaBg: "bg-white hover:bg-white/90",
    ctaText: "text-neutral-900",
    ctaHover: "",
    logoMarkBg: "bg-white",
    logoMarkText: "text-neutral-900",
    logoText: "text-white",
    shuffleBg: "bg-white/10 hover:bg-white/20 backdrop-blur",
    shuffleText: "text-white",
    shuffleBorder: "border-white/20",
    shimmerBase: "#f4f4f5",
    shimmerHighlight: "#f0abfc",
  },
  {
    key: "paper",
    label: "Ancient Paper",
    icon: "ri-book-2-line",
    bgClass: "theme-paper",
    textPrimary: "text-[#3a2510]",
    textMuted: "text-[#5a3d1f]",
    chipBorder: "border-[#8a6535]/40",
    chipBg: "bg-[#f4e6c1]/60",
    chipText: "text-[#5a3d1f]",
    chipDot: "bg-[#7a4a1a]",
    navHover: "hover:text-[#3a2510]",
    signIn: "text-[#5a3d1f] hover:text-[#3a2510]",
    ctaBg: "bg-[#3a2510] hover:bg-[#26170a]",
    ctaText: "text-[#ecd9b0]",
    ctaHover: "",
    logoMarkBg: "bg-[#3a2510]",
    logoMarkText: "text-[#ecd9b0]",
    logoText: "text-[#3a2510]",
    shuffleBg: "bg-[#3a2510]/90 hover:bg-[#3a2510]",
    shuffleText: "text-[#ecd9b0]",
    shuffleBorder: "border-[#3a2510]/30",
    shimmerBase: "#3a2510",
    shimmerHighlight: "#a86e2c",
  },
  {
    key: "universe",
    label: "3D Universe",
    icon: "ri-planet-line",
    bgClass: "theme-universe",
    textPrimary: "text-white",
    textMuted: "text-white/70",
    chipBorder: "border-white/20",
    chipBg: "bg-white/5",
    chipText: "text-white/80",
    chipDot: "bg-cyan-300",
    navHover: "hover:text-white",
    signIn: "text-white/70 hover:text-white",
    ctaBg: "bg-gradient-to-r from-fuchsia-500 to-cyan-400 hover:from-fuchsia-400 hover:to-cyan-300",
    ctaText: "text-white",
    ctaHover: "",
    logoMarkBg: "bg-gradient-to-br from-fuchsia-400 to-cyan-300",
    logoMarkText: "text-neutral-900",
    logoText: "text-white",
    shuffleBg: "bg-white/10 hover:bg-white/20 backdrop-blur",
    shuffleText: "text-white",
    shuffleBorder: "border-white/25",
    shimmerBase: "#e9d5ff",
    shimmerHighlight: "#67e8f9",
  },
  {
    key: "cloth",
    label: "Cloth",
    icon: "ri-t-shirt-line",
    bgClass: "theme-cloth",
    textPrimary: "text-[#3d2f1f]",
    textMuted: "text-[#6b5638]",
    chipBorder: "border-[#a08557]/40",
    chipBg: "bg-white/40",
    chipText: "text-[#6b5638]",
    chipDot: "bg-[#a86b3b]",
    navHover: "hover:text-[#3d2f1f]",
    signIn: "text-[#6b5638] hover:text-[#3d2f1f]",
    ctaBg: "bg-[#3d2f1f] hover:bg-[#241a10]",
    ctaText: "text-[#efe6d8]",
    ctaHover: "",
    logoMarkBg: "bg-[#3d2f1f]",
    logoMarkText: "text-[#efe6d8]",
    logoText: "text-[#3d2f1f]",
    shuffleBg: "bg-[#3d2f1f]/90 hover:bg-[#3d2f1f]",
    shuffleText: "text-[#efe6d8]",
    shuffleBorder: "border-[#3d2f1f]/30",
    shimmerBase: "#3d2f1f",
    shimmerHighlight: "#b98a4d",
  },
  {
    key: "silver",
    label: "Silver Metal",
    icon: "ri-scales-3-line",
    bgClass: "theme-silver",
    textPrimary: "text-neutral-900",
    textMuted: "text-neutral-700",
    chipBorder: "border-neutral-400/50",
    chipBg: "bg-white/40",
    chipText: "text-neutral-800",
    chipDot: "bg-neutral-900",
    navHover: "hover:text-neutral-900",
    signIn: "text-neutral-700 hover:text-neutral-900",
    ctaBg: "bg-neutral-900 hover:bg-neutral-800",
    ctaText: "text-white",
    ctaHover: "",
    logoMarkBg: "bg-neutral-900",
    logoMarkText: "text-white",
    logoText: "text-neutral-900",
    shuffleBg: "bg-neutral-900/90 hover:bg-neutral-900",
    shuffleText: "text-white",
    shuffleBorder: "border-neutral-900/30",
    shimmerBase: "#171717",
    shimmerHighlight: "#f5f5f5",
  },
];

interface TiltHeadlineProps {
  textPrimary: string;
}

function TiltHeadline({ textPrimary }: TiltHeadlineProps) {
  const ref = React.useRef<HTMLHeadingElement>(null);

  const px = useMotionValue(0);
  const py = useMotionValue(0);

  const sx = useSpring(px, { stiffness: 120, damping: 18, mass: 0.6 });
  const sy = useSpring(py, { stiffness: 120, damping: 18, mass: 0.6 });

  const rotateX = useTransform(sy, [-0.5, 0.5], [6, -6]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-8, 8]);

  const handleMove = (e: React.MouseEvent<HTMLHeadingElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    px.set((e.clientX - rect.left) / rect.width - 0.5);
    py.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleLeave = () => {
    px.set(0);
    py.set(0);
  };

  return (
    <div
      className="pointer-events-auto"
      style={{ perspective: 1200 }}
    >
      <motion.h1
        ref={ref}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
        }}
        className={`max-w-4xl font-heading text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.05] will-change-transform ${textPrimary}`}
      >
        <span className="headline-shimmer">
          Move your cursor.
          <br />
          Leave a trail of joy.
        </span>
      </motion.h1>
    </div>
  );
}

export default function Home() {
  const [themeIndex, setThemeIndex] = React.useState(0);
  const theme = themes[themeIndex];
  const nextTheme = themes[(themeIndex + 1) % themes.length];

  const shuffleTheme = () => {
    setThemeIndex((i) => (i + 1) % themes.length);
  };

  const items = trailEmojis.map((emoji, index) => (
    <span
      key={index}
      role="img"
      aria-label="trail-emoji"
      className="block w-full text-center leading-none select-none"
      style={{ fontSize: 96 }}
    >
      {emoji}
    </span>
  ));

  const shimmerStyle = {
    ["--shimmer-base" as string]: theme.shimmerBase,
    ["--shimmer-highlight" as string]: theme.shimmerHighlight,
  } as React.CSSProperties;

  return (
    <CursorImageTrail
      items={items}
      itemSize={110}
      trailLength={10}
      spawnDistance={70}
      rotationRange={30}
      className={`min-h-screen w-full cursor-none transition-colors duration-700 ${theme.bgClass}`}
      style={shimmerStyle}
    >
      <header className="relative z-20 w-full pointer-events-auto">
        <nav className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-5">
          <a
            href="#"
            className={`flex items-center gap-2 font-heading text-lg font-semibold tracking-tight cursor-pointer ${theme.logoText}`}
          >
            <span className={`w-8 h-8 flex items-center justify-center rounded-md text-base ${theme.logoMarkBg} ${theme.logoMarkText}`}>
              ✦
            </span>
            Trailplay
          </a>

          <ul className={`hidden md:flex items-center gap-8 text-sm ${theme.textMuted}`}>
            {navLinks.map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  className={`transition-colors cursor-pointer whitespace-nowrap ${theme.navHover}`}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <a
              href="#"
              className={`hidden sm:inline-flex text-sm transition-colors cursor-pointer whitespace-nowrap ${theme.signIn}`}
            >
              Sign in
            </a>
            <a
              href="#"
              className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${theme.ctaBg} ${theme.ctaText}`}
            >
              Get started
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </nav>
      </header>

      <div className="relative z-10 flex min-h-[calc(100vh-88px)] flex-col items-center justify-center px-6 text-center pointer-events-none">
        <TiltHeadline textPrimary={theme.textPrimary} />

        <p className={`mt-6 max-w-xl text-base md:text-lg ${theme.textMuted}`}>
          A tiny, delightful cursor emoji trail — drop it into any section and
          watch the page come alive as visitors explore.
        </p>

        <div className="mt-8 pointer-events-auto">
          <button
            type="button"
            onClick={shuffleTheme}
            title={`Switch to ${nextTheme.label}`}
            className={`inline-flex items-center gap-2 rounded-md border px-4 py-2.5 text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${theme.shuffleBg} ${theme.shuffleText} ${theme.shuffleBorder}`}
          >
            <i className={theme.icon}></i>
            <span>{theme.label}</span>
            <i className="ri-shuffle-line text-xs opacity-80"></i>
          </button>
        </div>
      </div>
    </CursorImageTrail>
  );
}